import BankIcon from 'assets/svg/icons/bankIcon';
import { Modal, type ModalApi } from 'components/atoms/Modal';
import UseEffect from 'components/atoms/UseEffect';
import CustomLoader from 'components/molecules/CustomLoader.component';
import ModalHeading from 'components/molecules/ModalHeading';
import SuccessFeedbackWithConfettiBurst from 'components/molecules/SuccessFeedbackWithConfettiBurst.component';
import MoneyTransfer, {
  type MoneyTransferInitialData,
} from 'components/pages/Banks/Wallet/MoneyTransfer';
import { WalletOnboarding } from 'components/pages/Banks/WalletOnboarding/WalletOnboarding.component';
import useCanAccessAccountableBanking from 'hooks/Bank/useCanAccessAccountableBanking';
import useGetConnectors from 'hooks/Bank/useGetConnectors';
import useHasAccountableBanking from 'hooks/Bank/useHasAccountableBanking';
import useInvalidateTransferDependencies from 'hooks/Bank/useInvalidateTransferDependencies';
import useQueryParams from 'hooks/shared/useQueryParams';
import { worldThemeContext } from 'hooks/shared/useWorldTheme';
import useCustomNavigate from 'hooks/useCustomeNavigate';
import useModal from 'hooks/useModal';
import { MoneyTransferContext } from 'hooks/useTransfer';
import { motion } from 'motion/react';
import { useRef, type FC, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { color } from 'types/shared/worlds';
import { getTracingEvents } from 'utils/amplitude';
import { WEBAPP_BANKING_PAYMENTTRANSFER_SUCCESSFULTRANSFER } from 'utils/amplitude/events/bank.amplitude';
import { fadeTransition, isWeb } from 'utils/constants';

const tracingEvents = getTracingEvents({
  onTransferSuccess: WEBAPP_BANKING_PAYMENTTRANSFER_SUCCESSFULTRANSFER,
});

const MoneyTransferProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation();

  const qs = useQueryParams();

  const successfulPaymentModalRef = useRef<ModalApi>();

  const [modalContent, modalApi] = useModal({
    width: '2xl',
    name: 'Accountable Banking payment transfer modal',
    color: 'bank',
  });

  const navigate = useCustomNavigate();

  const invalidateTransferDeps = useInvalidateTransferDependencies();

  const { isLoading } = useGetConnectors();

  const canAccessAccountableBanking = useCanAccessAccountableBanking();

  const hasAccountableBanking = useHasAccountableBanking();

  const transfer = (
    initialData: MoneyTransferInitialData = {},
    config?: {
      theme?: color;
      onSuccess?: () => void;
      isStandingOrderPayment?: boolean;
    },
  ) => {
    const { theme = 'bank', isStandingOrderPayment = false } = config || {};

    modalApi.open(
      () => (
        <worldThemeContext.Provider value={theme || 'bank'}>
          {isLoading ? (
            <div className="min-h-[300px] flex items-center justify-center">
              <CustomLoader />
            </div>
          ) : !isWeb ? (
            <ModalHeading
              title={t('accountable_banking.forbid_expert_transfer.title')}
              subtitle={t(
                'accountable_banking.forbid_expert_transfer.subtitle',
              )}
              Icon={BankIcon}
            />
          ) : !canAccessAccountableBanking ? (
            <ModalHeading
              title={t('accountable_banking')}
              subtitle={t('accountable_banking.no_access')}
              Icon={BankIcon}
            />
          ) : !hasAccountableBanking ? (
            <WalletOnboarding />
          ) : (
            <MoneyTransfer
              initialData={initialData}
              onTransferSuccess={() => {
                modalApi.close();
                config?.onSuccess?.();
                setTimeout(
                  () => successfulPaymentModalRef.current?.openModal(),
                  1000,
                );
              }}
              isStandingOrderPayment={isStandingOrderPayment}
            />
          )}
        </worldThemeContext.Provider>
      ),
      { color: theme, name: 'Wallet send money modal' },
    );
  };

  return (
    <MoneyTransferContext.Provider value={{ transfer }}>
      {modalContent}

      <Modal
        name="Successful payment"
        apiRef={successfulPaymentModalRef}
        className="!max-w-[44rem]"
        color="bank"
        onClose={
          qs.successfulPayment === 'true'
            ? () => {
                navigate('/banks');

                invalidateTransferDeps();
              }
            : () => {}
        }
        defaultOpen={
          qs.successfulPayment === 'true' ||
          qs.successfulBasketPayment === 'true'
        }
        render={() => (
          <UseEffect
            effect={() => tracingEvents.onTransferSuccess()}
            deps={[]}
            render={() => (
              <motion.div
                {...fadeTransition}
                key="3"
                className="flex items-center flex-col relative min-h-[400px] w-full"
              >
                <SuccessFeedbackWithConfettiBurst
                  message={
                    qs.successfulBasketPayment === 'true'
                      ? t(
                          'accountable_banking.expenses_basket.payment.success_msg',
                        )
                      : qs.isScheduledPayment === 'true'
                      ? t('accountable_banking.scheduled_payment.success_msg')
                      : t('accountable_banking.successful_payment_msg')
                  }
                />
              </motion.div>
            )}
          />
        )}
      />

      {children}
    </MoneyTransferContext.Provider>
  );
};

export default MoneyTransferProvider;
